// @MX:ANCHOR: [AUTO] 실물인증서 카드 API 모듈 — china-inapi /certificate-cards 15 endpoint
// @MX:REASON: [AUTO] CertificateCard 발번/이력/정품조회/비용/판매견적/제작견적 화면 공통 진입점 (fan_in >= 3). SPEC-CHINA-CERT-CARD-ISSUE-001 v0.5.2 + SPEC-CHINA-CERT-CARD-COST-001 + SPEC-CHINA-CERT-CARD-SALES-QUOTATION-001 + SPEC-CHINA-CERT-CARD-COST-QUOTATION-001.
// @MX:SPEC: SPEC-CHINA-CERT-CARD-SALES-QUOTATION-001
// @MX:NOTE: [AUTO] BE 신규 query param/body field 추가 시 이 wrapper도 반드시 동기화 필요.

import chinaInapi from './chinaInapi';

/**
 * 실물인증서 카드 API 래퍼
 * SPEC-CHINA-CERT-CARD-ISSUE-001 — 발번·엑셀·배치이력·정품조회
 */
const certificateCardApi = {
  /**
   * GTIN 후보 목록 조회 (GS1 mod-10 통과분만 반환)
   * GET /certificate-cards/gtin-candidates
   * REQ-BE-GTIN-001~003
   * @param {string|undefined} keyword - 검색 키워드 (GTIN 또는 제품명)
   * @returns {Promise}
   */
  getGtinCandidates(keyword) {
    const params = {};
    if (keyword) params.keyword = keyword;
    return chinaInapi.get('/certificate-cards/gtin-candidates', { params });
  },

  /**
   * 실물인증서 카드 발번 배치 생성
   * POST /certificate-cards/batches
   * REQ-BE-ISSUE-001~010, REQ-BE-VALID-001~002
   * @param {Object} dto
   * @param {'inbound'|'direct'} dto.issueMode - 발급 모드
   * @param {Array<{gtin:string, batchNumber:string, quantity:number, multiplier:number, issueQuantity?:number}>} [dto.inboundEntries]
   *   inbound 모드 전용 — 엔트리별 {gtin, batchNumber, quantity(원입고수량), multiplier(배수, 기본30=DEFAULT_MULTIPLIER), issueQuantity(이번 발급 장수, 미전달=잔여 전량)}.
   *   BE가 quantity×multiplier 산출. issueQuantity > remaining 시 skipped reason='exceeds_remaining'.
   *   동일 gtin+batchNumber E11000 시 해당 엔트리 skip(reason='conflict').
   * @param {Object} [dto.gtinCounts] - direct 모드 전용 — GTIN별 발급 장수 { gtin: count }
   * @param {string[]} [dto.excelColumns] - 엑셀 포함 선택 컬럼 (productName|batchNumber|customer|printedAt)
   * @param {'v1'|'v2'} [dto.cardVersion] - 카드 디자인 버전 (v1/v2, 미전달 시 BE 기본 v1). 엑셀 2번째 시트 인쇄카드 샘플 디자인 결정.
   * @returns {Promise<{batchId, status, issueStatus, successCount, failCount, skipped:[{gtin,batchNumber,reason:'conflict'|'already_completed'|'exceeds_remaining'}]}>}
   */
  createBatch(dto) {
    return chinaInapi.post('/certificate-cards/batches', dto);
  },

  /**
   * 발급 배치 목록 조회 (페이지네이션)
   * GET /certificate-cards/batches
   * REQ-BE-BATCH-001
   * @param {Object} params - { page, limit }
   * @returns {Promise}
   */
  getBatches(params = {}) {
    return chinaInapi.get('/certificate-cards/batches', { params });
  },

  /**
   * 발급 배치 상세 조회
   * GET /certificate-cards/batches/:batchId
   * REQ-BE-BATCH-002
   * @param {string} batchId
   * @returns {Promise}
   */
  getBatchDetail(batchId) {
    return chinaInapi.get(`/certificate-cards/batches/${encodeURIComponent(batchId)}`);
  },

  /**
   * 배치 제작 카드 경량 목록 조회 (난수/GTIN/Lot 전량 — 드로워 클라이언트 검색용)
   * GET /certificate-cards/batches/:batchId/cards
   * @param {string} batchId
   * @returns {Promise<{ batchId, total, truncated, items: [{randomNo, gtin, inboundBatchNumber}] }>}
   */
  getBatchCards(batchId) {
    return chinaInapi.get(`/certificate-cards/batches/${encodeURIComponent(batchId)}/cards`);
  },

  /**
   * 발급 배치 엑셀 다운로드 (멱등 — 재발급 없음)
   * GET /certificate-cards/batches/:batchId/excel
   * REQ-BE-XLSX-001~006
   * @param {string} batchId
   * @returns {Promise} responseType: 'blob'
   */
  downloadExcel(batchId, columns, requiredOnly = false) {
    // columns: 이력 재다운로드 컬럼 재선택(배열 또는 콤마문자열). 미전달 시 배치 저장 컬럼 사용(BE 폴백).
    // requiredOnly: 선택 항목 전체 해제 시 true → 저장 컬럼 폴백 없이 필수값(난수/GTIN)만 출력.
    const params = {};
    if (columns && columns.length) {
      params.columns = Array.isArray(columns) ? columns.join(',') : columns;
    }
    if (requiredOnly) {
      params.requiredOnly = 'true';
    }
    return chinaInapi.get(`/certificate-cards/batches/${encodeURIComponent(batchId)}/excel`, {
      responseType: 'blob',
      params,
    });
  },

  /**
   * 탭2: 미발급 GTIN 목록 조회 (페이지네이션)
   * GET /certificate-cards/unissued-gtins
   * REQ-BE-UNISSUED-001, REQ-BE-UNISSUED-003
   * @param {Object} params - { page, limit } (기본 50, 최대 200)
   * @returns {Promise} { data: { gtin, productName }[], meta: { total, page, limit } }
   */
  getUnissuedGtins(params = {}) {
    return chinaInapi.get('/certificate-cards/unissued-gtins', { params });
  },

  /**
   * 탭①: 미제작 배치 목록 조회 (페이지네이션) — v0.4.1 shape 변경
   * GET /certificate-cards/unissued-inbounds
   * REQ-BE-INBOUND-001, REQ-BE-INBOUND-001a, REQ-FE-BULK-004
   * @param {Object} params - { gtin?, dateFrom?, dateTo?, page, limit, includeCompleted? }
   *   gtin: GTIN 검색어 (exact/prefix)
   *   dateFrom: 입고일 시작 YYYYMMDD 8자리
   *   dateTo: 입고일 종료 YYYYMMDD 8자리
   *   page: 페이지 번호
   *   limit: 페이지 크기 (기본 50)
   *   includeCompleted: true 시 제작완료 포함 조회 (기본 false — remaining>0만)
   * @returns {Promise} { rows: [{gtin, productName, batchNumber, quantity, warehousingDate, multiplier, cap, issuedCount, remaining, status}],
   *                      pagination: {total, page, limit},
   *                      grandTotalPrintCount: number }
   */
  getUnissuedInbounds(params = {}) {
    // gtin/dateFrom/dateTo/page/limit/includeCompleted 모두 전달 (undefined 키는 axios가 자동 제거)
    const query = {};
    if (params.gtin) query.gtin = params.gtin;
    if (params.gtins) query.gtins = params.gtins; // GTIN 멀티선택(콤마구분) — BE gtins[] 필터
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.page) query.page = params.page;
    if (params.limit) query.limit = params.limit;
    // includeCompleted: 명시적 true 시에만 전달 (기본값 false는 BE default와 동일 — 불필요한 쿼리 파라미터 제거)
    if (params.includeCompleted === true) query.includeCompleted = true;
    // statuses: 상태 멀티선택 필터(콤마구분 또는 배열) — BE statuses[] 필터. 존재 시 기본필터 대체.
    if (params.statuses && params.statuses.length) {
      query.statuses = Array.isArray(params.statuses) ? params.statuses.join(',') : params.statuses;
    }
    // productName(contains)·productSpec(형명 contains)·batchNumber(prefix) 검색 — BE 신규 필터
    if (params.productName && params.productName.trim()) query.productName = params.productName.trim();
    if (params.productSpec && params.productSpec.trim()) query.productSpec = params.productSpec.trim();
    if (params.batchNumber && params.batchNumber.trim()) query.batchNumber = params.batchNumber.trim();
    // 입고수량/잔여수량 범위 고급필터 — BE quantityMin/Max·remainingMin/Max (0 포함 유효값, null/'' 제외)
    if (params.quantityMin != null && params.quantityMin !== '') query.quantityMin = params.quantityMin;
    if (params.quantityMax != null && params.quantityMax !== '') query.quantityMax = params.quantityMax;
    if (params.remainingMin != null && params.remainingMin !== '') query.remainingMin = params.remainingMin;
    if (params.remainingMax != null && params.remainingMax !== '') query.remainingMax = params.remainingMax;
    return chinaInapi.get('/certificate-cards/unissued-inbounds', { params: query });
  },

  /**
   * 발급 배치 확정 (draft → confirmed)
   * PATCH /certificate-cards/batches/:batchId/confirm
   * REQ-BE-CONFIRM-001
   * @param {string} batchId
   * @returns {Promise}
   */
  confirmBatch(batchId) {
    return chinaInapi.patch(`/certificate-cards/batches/${encodeURIComponent(batchId)}/confirm`);
  },

  /**
   * 발급 배치 삭제 (draft 상태만 허용 — confirmed 409)
   * DELETE /certificate-cards/batches/:batchId
   * REQ-BE-DELETE-001
   * @param {string} batchId
   * @returns {Promise}
   */
  deleteBatch(batchId) {
    return chinaInapi.delete(`/certificate-cards/batches/${encodeURIComponent(batchId)}`);
  },

  /**
   * 실물인증서 카드 다중 필터 검색 (목록 조회)
   * GET /certificate-cards/search
   * SPEC-CHINA-CERT-TRACE-SNAPSHOT-001 §4.9 — 제작일자/배치번호/GTIN 필터 + 페이지네이션
   * SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 — 신규 `q`(unified contains) 파라미터 추가.
   *   기존 `gtin`/`inboundBatchNumber`는 PREFIX 그대로 유지(verify 후방호환, 의미 변경 없음).
   * @param {Object} params
   * @param {string} [params.q]           - [신규] 통합 검색어. randomNo/gtin/inboundBatchNumber에 $or contains 매칭(Step3 전용).
   * @param {string} [params.createdFrom] - 제작일자 시작 YYYYMMDD 8자리
   * @param {string} [params.createdTo]   - 제작일자 종료 YYYYMMDD 8자리
   * @param {string} [params.inboundBatchNumber] - 배치번호 (prefix, verify LIST)
   * @param {string} [params.gtin]        - GTIN (prefix, verify LIST)
   * @param {number} [params.page]        - 페이지 번호 (1부터)
   * @param {number} [params.limit]       - 페이지 크기 (기본 20)
   * @returns {Promise<{ data: { items: object[], total, page, limit } }>}
   */
  searchCards(params = {}) {
    // 허용된 필드만 전달 (화이트리스트, [[feedback_fe_api_wrapper_param_whitelist]])
    const query = {};
    if (params.q && params.q.trim()) query.q = params.q.trim();
    if (params.createdFrom) query.createdFrom = params.createdFrom;
    if (params.createdTo) query.createdTo = params.createdTo;
    if (params.inboundBatchNumber) query.inboundBatchNumber = params.inboundBatchNumber;
    if (params.gtin) query.gtin = params.gtin;
    // 고급필터: 인쇄일자 범위 · 발급배치ID prefix · 확인상태(draft|confirmed)
    if (params.printedFrom) query.printedFrom = params.printedFrom;
    if (params.printedTo) query.printedTo = params.printedTo;
    if (params.issueBatchId && params.issueBatchId.trim()) query.issueBatchId = params.issueBatchId.trim();
    if (params.confirmStatus) query.confirmStatus = params.confirmStatus;
    if (params.page) query.page = params.page;
    if (params.limit) query.limit = params.limit;
    return chinaInapi.get('/certificate-cards/search', { params: query });
  },

  /**
   * 실물인증서 카드 인쇄 완료 마킹 (XR160 PRO 카드 프린터 실물 인쇄 후 수동 확정)
   * PATCH /certificate-cards/cards/:randomNo/printed
   * SPEC-CHINA-CERT-CARD-PRINT-001
   * @param {string} randomNo - 10자리 숫자 (No. 접두 포함 또는 미포함)
   * @returns {Promise}
   */
  markCardPrinted(randomNo) {
    // verifyCard와 동일하게 No. 접두 제거 정규화
    const normalized = randomNo.replace(/^No\./i, '').trim();
    return chinaInapi.patch(`/certificate-cards/cards/${encodeURIComponent(normalized)}/printed`);
  },

  /**
   * 실물카드 인쇄용 데이터 조회 (작업지시서 연결 카드의 7개 필드값, 읽기 전용 SSOT)
   * GET /certificate-cards/cards/:randomNo/print-data
   * SPEC-CHINA-CERT-CARD-PRINT-001 (필드 인쇄 확장)
   * @param {string} randomNo - 10자리 숫자 (No. 접두 포함 또는 미포함)
   * @returns {Promise<{ randomNo, gtin, printedAt, linked: boolean,
   *   source: 'manual'|'hengjiu'|null,
   *   fields: null | { patientName, toothPosition, doctorName, orderNo, date, labName, hospitalName } }>}
   *   linked=false면 작업지시서 미연결 카드 — fields는 null, 인쇄 불가.
   */
  getCardPrintData(randomNo) {
    // verifyCard/markCardPrinted와 동일하게 No. 접두 제거 정규화
    const normalized = randomNo.replace(/^No\./i, '').trim();
    return chinaInapi.get(`/certificate-cards/cards/${encodeURIComponent(normalized)}/print-data`);
  },

  /**
   * 실물인증서 카드 정품 조회 (난수 단건)
   * GET /certificate-cards/verify/:randomNo
   * REQ-BE-VERIFY-001~004, REQ-FE-TRACE-005
   * @param {string} randomNo - 10자리 숫자 (No. 접두 포함 또는 미포함)
   * @param {Object} [options]
   * @param {boolean} [options.liveSales=false] - true 시 ?liveSales=true 파라미터 추가 (딜러 판매 실시간 재조회 [SHOULD])
   * @returns {Promise<{ valid: boolean, card: object, traceSnapshot: object|null }>}
   */
  verifyCard(randomNo, { liveSales = false } = {}) {
    // REQ-FE-VERIFY-003: No. 접두 제거 정규화
    const normalized = randomNo.replace(/^No\./i, '').trim();
    const params = liveSales ? { liveSales: true } : {};
    return chinaInapi.get(`/certificate-cards/verify/${encodeURIComponent(normalized)}`, { params });
  },

  // ─── 카드 디자인 레지스트리 (SPEC-CHINA-CERT-CARD-DESIGN-REGISTRY-001) ─────────

  /**
   * 실물인증서 카드 디자인 목록 조회 (활성 디자인만, sortOrder asc → code asc)
   * GET /certificate-cards/designs
   * @returns {Promise<{ data: { items: Array<{
   *   code:string, labelKo:string, labelEn:string, labelZh:string,
   *   imageKey:string, imageUrl:string, mime:string, width:number, height:number,
   *   overlay:{ gtin:{topPct:number, rightPct:number, widthPct:number, fontPx:number},
   *     randomNo:{bottomPct:number, leftPct:number(CENTER x좌표, left-edge 아님), fontPx:number} },
   *   sortOrder:number, active:boolean
   * }> } }>}
   */
  listCertCardDesigns() {
    return chinaInapi.get('/certificate-cards/designs');
  },

  /**
   * 실물인증서 카드 디자인 신규 등록 (multipart/form-data)
   * POST /certificate-cards/designs
   * 허용 형식: PNG, JPEG만 (webp 등은 BE가 400 CD-FILE-001로 거부)
   * @param {FormData} formData - 필드: file, code, labelKo, labelEn, labelZh,
   *   gtinTopPct, gtinRightPct, gtinWidthPct, randomNoBottomPct,
   *   randomNoLeftPct?(선택, 기본 50 — 난수 라벨의 CENTER x좌표, left-edge 아님, 0~100),
   *   gtinFontPx?(선택, 기본 8, 6~40), randomNoFontPx?(선택, 기본 10, 6~40), sortOrder?(선택)
   * @returns {Promise} 201 — 생성된 디자인 단건 (listCertCardDesigns 항목과 동일 shape)
   */
  uploadCertCardDesign(formData) {
    return chinaInapi.post('/certificate-cards/designs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ─── 비용 관리 (SPEC-CHINA-CERT-CARD-COST-001 v0.4.0) ─────────────────────

  /**
   * 배치 비용 조회
   * GET /certificate-cards/batches/:batchId/cost
   * AC-BE-GET-001~002
   * cost=null 이면 { batchId, cost: null } 반환 (404 아님).
   * fapiaoBlob 존재 시 fapiaoUrl (SAS 30분) 포함.
   * @param {string} batchId
   * @returns {Promise<{ batchId: string, cost: object|null }>}
   */
  getCost(batchId) {
    return chinaInapi.get(`/certificate-cards/batches/${encodeURIComponent(batchId)}/cost`);
  },

  /**
   * 배치 비용 등록/수정 (낙관적 잠금, ADMIN 전용)
   * PATCH /certificate-cards/batches/:batchId/cost
   * AC-BE-PATCH-001~003a
   * @param {string} batchId
   * @param {Object} body
   * @param {number} [body.costVersion] - 낙관적 잠금 버전 (최초: 0 또는 생략)
   * @param {number|null} [body.printingCost] - 제작비용 CNY
   * @param {number|null} [body.exchangeRate] - 환율 (1 CNY = N KRW)
   * @param {number|null} [body.salesAmount] - 판매금액 KRW
   * @param {string|null} [body.fapiaoDate] - 파표 일자 YYYY-MM-DD
   * @param {string} body.reason - 변경 사유 (필수)
   * @returns {Promise}
   */
  patchCost(batchId, body) {
    return chinaInapi.patch(`/certificate-cards/batches/${encodeURIComponent(batchId)}/cost`, body);
  },

  /**
   * 파피아오(发票) 이미지 업로드 (multipart/form-data, ADMIN 전용)
   * POST /certificate-cards/batches/:batchId/cost/fapiao
   * AC-STORAGE-001~003
   * 허용 형식: .pdf .png .jpg .jpeg, 최대 10MB
   * @param {string} batchId
   * @param {File} file - 업로드할 파일
   * @returns {Promise}
   */
  uploadFapiaoImage(batchId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return chinaInapi.post(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/cost/fapiao`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /**
   * 파피아오(发票) 이미지 삭제 (ADMIN 전용)
   * DELETE /certificate-cards/batches/:batchId/cost/fapiao
   * AC-STORAGE-004
   * @param {string} batchId
   * @returns {Promise}
   */
  deleteFapiaoImage(batchId) {
    return chinaInapi.delete(`/certificate-cards/batches/${encodeURIComponent(batchId)}/cost/fapiao`);
  },

  /**
   * 한국 재판매 견적서 문서 업로드 (multipart/form-data, ADMIN 전용)
   * POST /certificate-cards/batches/:batchId/cost/sales-quotation
   * 허용 형식: .pdf .png .jpg .jpeg .gif .webp, 최대 10MB
   * @param {string} batchId
   * @param {File} file - 업로드할 파일
   * @returns {Promise}
   */
  uploadSalesQuotation(batchId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return chinaInapi.post(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/cost/sales-quotation`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /**
   * 한국 재판매 견적서 문서 삭제 (ADMIN 전용)
   * DELETE /certificate-cards/batches/:batchId/cost/sales-quotation
   * @param {string} batchId
   * @returns {Promise} 204 No Content
   */
  deleteSalesQuotation(batchId) {
    return chinaInapi.delete(`/certificate-cards/batches/${encodeURIComponent(batchId)}/cost/sales-quotation`);
  },

  /**
   * 비용 감사 이력 조회 (페이지네이션, 최신순)
   * GET /certificate-cards/batches/:batchId/cost/history
   * AC-BE-HISTORY-001
   * @param {string} batchId
   * @param {number} [page=1]
   * @param {number} [limit=20]
   * @returns {Promise<{ batchId: string, total: number, items: object[] }>}
   */
  getCostHistory(batchId, page = 1, limit = 20) {
    return chinaInapi.get(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/cost/history`,
      { params: { page, limit } },
    );
  },

  // ─── 견적서 초안 (SPEC-CHINA-CERT-QUOTATION-DRAFT-001) ────────────────────

  /**
   * 견적서 초안 신규 차수 생성 (PDF 동기 렌더 + 업로드, ADMIN 전용)
   * POST /certificate-cards/batches/:batchId/quotation-drafts
   * REQ-BE-QD-API-001, spec §7.3
   * @param {string} batchId
   * @param {Object} body
   * @param {Object} body.header - { quotationDate, recipient, attention?, paymentTerms, validity, subject }
   * @param {number} [body.documentExchangeRate] - 견적일 기준 환율(1 CNY = N KRW). cost.exchangeRate와 독립(D8)
   * @param {Array<Object>} body.items - {category?, itemName, description?, unitPriceCny?, unitPriceKrw?,
   *   quantity, priceSource:'QUOTATION'|'MANUAL', quotationTier?}
   * @param {string[]} body.terms - 견적조건(하단 번호 목록)
   * @returns {Promise} 생성된 초안 doc. amountKrw·totals·revision·pdf*는 BE 산출값(클라 값 무시).
   */
  createQuotationDraft(batchId, body) {
    return chinaInapi.post(`/certificate-cards/batches/${encodeURIComponent(batchId)}/quotation-drafts`, body);
  },

  /**
   * 견적서 초안 차수 목록 조회 (최신순, pdfUrl 미포함)
   * GET /certificate-cards/batches/:batchId/quotation-drafts
   * @param {string} batchId
   * @returns {Promise<{ batchId: string, items: object[] }>}
   */
  getQuotationDrafts(batchId) {
    return chinaInapi.get(`/certificate-cards/batches/${encodeURIComponent(batchId)}/quotation-drafts`);
  },

  /**
   * 견적서 초안 단건 조회 (pdfUrl SAS 30분 포함)
   * GET /certificate-cards/batches/:batchId/quotation-drafts/:revision
   * @param {string} batchId
   * @param {number} revision
   * @returns {Promise}
   */
  getQuotationDraft(batchId, revision) {
    return chinaInapi.get(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/quotation-drafts/${encodeURIComponent(revision)}`,
    );
  },

  /**
   * 견적서 초안 상태 전이 (ADMIN 전용, 낙관락 version 필수)
   * PATCH /certificate-cards/batches/:batchId/quotation-drafts/:revision/status
   * @param {string} batchId
   * @param {number} revision
   * @param {Object} body - { status, reason?, version }
   * @returns {Promise}
   */
  updateQuotationDraftStatus(batchId, revision, body) {
    return chinaInapi.patch(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/quotation-drafts/${encodeURIComponent(revision)}/status`,
      body,
    );
  },

  /**
   * 견적서 초안 확정 → cost.salesQuotationBlob 승격 (ADMIN 전용, CONFIRMED 차수만 가능)
   * POST /certificate-cards/batches/:batchId/quotation-drafts/:revision/finalize
   * @param {string} batchId
   * @param {number} revision
   * @returns {Promise}
   */
  finalizeQuotationDraft(batchId, revision) {
    return chinaInapi.post(
      `/certificate-cards/batches/${encodeURIComponent(batchId)}/quotation-drafts/${encodeURIComponent(revision)}/finalize`,
    );
  },

  // ─── 고객사 재판매 견적표 (SPEC-CHINA-CERT-CARD-SALES-QUOTATION-001) ─────────

  /**
   * 회사 견적 조회 (optional previewQuantity → matchStatus 동봉)
   * GET /certificate-cards/manufacturer/:companyId/sales-quotation[?previewQuantity=N]
   * REQ-BE-GET-001~002, AC-BE-GET-001~002
   * @param {string} companyId - JWT sub(=회사 식별자). 프론트는 jwtDecoder로 도출.
   * @param {number|undefined} previewQuantity - 배치 totalCount 기반 미리보기 수량. 미전달 시 미리보기 없음.
   * @returns {Promise<{ quotation: object|null, salesAmountPreview?: { matchStatus, unitPrice, salesAmount } }>}
   *   견적 미설정 시 quotation=null (404 아님). REQ-CALC-003: NO_MATCH 시 salesAmount=null (0 coerce 금지).
   */
  getSalesQuotation(companyId, previewQuantity) {
    const params = {};
    // previewQuantity=0은 유효하지 않으므로 falsy 체크로 충분
    if (previewQuantity != null && previewQuantity > 0) {
      params.previewQuantity = previewQuantity;
    }
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/sales-quotation`,
      { params },
    );
  },

  /**
   * 회사 견적 저장/갱신 (ADMIN 전용, 낙관락 version 필수)
   * PUT /certificate-cards/manufacturer/:companyId/sales-quotation
   * REQ-BE-PUT-001~003, AC-BE-PUT-001~003
   * @param {string} companyId - JWT sub. BE가 path param + JWT sub 교차검증.
   * @param {Object} body
   * @param {number} body.version - 낙관락 버전 (최초 0, 이후 직전 응답 version)
   * @param {string} [body.reason] - 변경 사유 (선택)
   * @param {Array<{minQty:number, maxQty:number|null, unitPrice:number}>} body.tiers - 수량 구간 배열
   * @returns {Promise} 저장된 견적 doc (version 증가). 낙관락 충돌 → 409.
   */
  putSalesQuotation(companyId, body) {
    return chinaInapi.put(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/sales-quotation`,
      body,
    );
  },

  /**
   * 견적 변경이력 조회 (auditTrail 페이지네이션, 최신순)
   * GET /certificate-cards/manufacturer/:companyId/sales-quotation/history
   * REQ-BE-HISTORY-001, AC-BE-HISTORY-001
   * @param {string} companyId
   * @param {number} [page=1]
   * @param {number} [limit=20]
   * @returns {Promise<{ items: object[], total: number, page: number, limit: number }>}
   */
  getSalesQuotationHistory(companyId, page = 1, limit = 20) {
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/sales-quotation/history`,
      { params: { page, limit } },
    );
  },

  // ─── 발주처(제작) 견적표 (SPEC-CHINA-CERT-CARD-COST-QUOTATION-001) ─────────────

  /**
   * 제작 견적 조회 (optional previewQuantity → printingCostPreview 동봉)
   * GET /certificate-cards/manufacturer/:companyId/cost-quotation[?previewQuantity=N]
   * @param {string} companyId - JWT sub(=회사 식별자). 프론트는 jwtDecoder로 도출.
   * @param {number|undefined} previewQuantity - 배치 totalCount 기반 미리보기 수량. 미전달 시 미리보기 없음.
   * @returns {Promise<{ quotation: object|null, printingCostPreview?: { matchStatus, unitPrice, printingCost } }>}
   *   견적 미설정 시 quotation=null (404 아님). NO_MATCH 시 printingCost=null (0 coerce 금지).
   */
  getCostQuotation(companyId, previewQuantity) {
    const params = {};
    // previewQuantity=0은 유효하지 않으므로 falsy 체크로 충분
    if (previewQuantity != null && previewQuantity > 0) {
      params.previewQuantity = previewQuantity;
    }
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/cost-quotation`,
      { params },
    );
  },

  /**
   * 제작 견적 저장/갱신 (ADMIN 전용, 낙관락 version 필수)
   * PUT /certificate-cards/manufacturer/:companyId/cost-quotation
   * @param {string} companyId - JWT sub. BE가 path param + JWT sub 교차검증.
   * @param {Object} body
   * @param {number} body.version - 낙관락 버전 (최초 0, 이후 직전 응답 version)
   * @param {string} [body.reason] - 변경 사유 (선택)
   * @param {Array<{minQty:number, maxQty:number|null, unitPrice:number}>} body.tiers - 수량 구간 배열 (단가: CNY)
   * @returns {Promise} 저장된 견적 doc (version 증가). 낙관락 충돌 → 409.
   */
  putCostQuotation(companyId, body) {
    return chinaInapi.put(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/cost-quotation`,
      body,
    );
  },

  /**
   * 제작 견적 변경이력 조회 (auditTrail 페이지네이션, 최신순)
   * GET /certificate-cards/manufacturer/:companyId/cost-quotation/history
   * @param {string} companyId
   * @param {number} [page=1]
   * @param {number} [limit=20]
   * @returns {Promise<{ items: object[], total: number, page: number, limit: number }>}
   */
  getCostQuotationHistory(companyId, page = 1, limit = 20) {
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/cost-quotation/history`,
      { params: { page, limit } },
    );
  },

  // ─── 연결 배치 (SPEC-CHINA-CERT-CARD-QUOTATION-UX-001 M1) ─────────────────

  /**
   * 판매 견적 연결 배치 조회
   * GET /certificate-cards/manufacturer/:companyId/sales-quotation/connected-batches
   * @param {string} companyId - JWT sub(=회사 식별자)
   * @param {'matched'|'applied'} view
   *   matched: 견적 구간에 매칭되고 salesAmount 미설정 배치
   *   applied: salesAmount가 채워진 배치 (근사치)
   * @param {number} [page=1] - 1-based 페이지 번호
   * @param {number} [pageSize=50] - 페이지당 항목 수 (최대 200)
   * @returns {Promise<{ items: MatchedBatchItem[]|AppliedBatchItem[], total: number, page: number, pageSize: number }>}
   *   matched items: { batchId, totalCount, status, unitPrice, amount, createdAt }
   *   applied items: { batchId, totalCount, status, salesAmount, createdAt }
   */
  getSalesConnectedBatches(companyId, view = 'matched', page = 1, pageSize = 50) {
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/sales-quotation/connected-batches`,
      { params: { view, page, pageSize } },
    );
  },

  /**
   * 제작 견적 연결 배치 조회
   * GET /certificate-cards/manufacturer/:companyId/cost-quotation/connected-batches
   * @param {string} companyId - JWT sub(=회사 식별자)
   * @param {'matched'|'applied'} view
   *   matched: 견적 구간에 매칭되고 printingCost 미설정 배치
   *   applied: printingCost가 채워진 배치 (근사치)
   * @param {number} [page=1] - 1-based 페이지 번호
   * @param {number} [pageSize=50] - 페이지당 항목 수 (최대 200)
   * @returns {Promise<{ items: MatchedBatchItem[]|AppliedBatchItem[], total: number, page: number, pageSize: number }>}
   *   matched items: { batchId, totalCount, status, unitPrice, amount, createdAt }
   *   applied items: { batchId, totalCount, status, printingCost, createdAt }
   */
  getCostConnectedBatches(companyId, view = 'matched', page = 1, pageSize = 50) {
    return chinaInapi.get(
      `/certificate-cards/manufacturer/${encodeURIComponent(companyId)}/cost-quotation/connected-batches`,
      { params: { view, page, pageSize } },
    );
  },

  /**
   * WMS 출고단위 엑셀 행을 GTIN으로 역조회 (dry-run, DB 쓰기 없음).
   * @param {Array<{productCode?:string, spec?:string, qty:number, lot:string}>} rows - 파싱된 WMS 행 목록
   * @returns {Promise} { rows: WmsResolvedRow[], summary }
   */
  // @MX:ANCHOR: [AUTO] wmsResolve — WMS→GTIN 역조회 진입점. FE 프리뷰 모달·제작 실행이 이 응답에 의존 (fan_in≥3).
  // @MX:REASON: [AUTO] SPEC-CHINA-CERT-CARD-WMS-RESOLVE-001 §7.3 계약 — inboundQuantity/multiplier/cap 필드를 직접 사용 (D1 규칙, 역산 금지).
  wmsResolve(rows) {
    return chinaInapi.post('/certificate-cards/wms-resolve', { rows });
  },

  // ─── 환자 자가발급 역연결 (수기 작업지시서 Step3 pre-fill) ─────────────────
  // @MX:NOTE: [AUTO] Step3 주 사용자는 기공소(DENTAL_LABORATORY)이므로 TenantGuard(제조사 전용)
  //   M4 EP(/products/admin/dentalmax/china/...)는 403이 된다(감사 A-2, 2026-07-21). issuance-status와
  //   동일하게 role 기반 래퍼 EP(/china/dental-lab/work-orders/cards/...)를 소비한다 — BE가 dentalmax
  //   테넌트로 in-process 위임한다. Step3 카드검색(searchCards)과 동일 axios 인스턴스(chinaInapi) 재사용.

  /**
   * 난수(随机码)에 환자 자가발급 인증서가 연결되어 있는지 조회(role 기반, 기공소 허용).
   * GET /china/dental-lab/work-orders/cards/:randomNo/self-issue → { success, data: {...} }
   * @param {string} randomNo - 10자리 난수
   * @returns {Promise} axios response. 소비측(ZirconiaToothInfoForm)이 response.data.data 로 언랩.
   *   data: { selfIssued: boolean, teeth?: number[], products?: [...], patient?: {...} }
   */
  getSelfIssuedCert(randomNo) {
    return chinaInapi.get(`/china/dental-lab/work-orders/cards/${encodeURIComponent(randomNo)}/self-issue`);
  },

  /**
   * 환자 자가발급 인증서 회수(证书回收) — 카드(난수) 단위, 멱등(role 기반, 기공소/운영자(XINLAIAN) 허용).
   * POST /china/dental-lab/work-orders/cards/:randomNo/recover-certificate → { success, data }
   * @param {string} randomNo - 10자리 난수
   * @returns {Promise}
   */
  recoverCertificate(randomNo) {
    return chinaInapi.post(`/china/dental-lab/work-orders/cards/${encodeURIComponent(randomNo)}/recover-certificate`);
  },

  // ─── 딜러사 대시보드 (SPEC-CHINA-DEALER-DASHBOARD-001) ─────────────────────

  /**
   * 딜러사 대시보드 집계 조회 (딜러 전용, 회사 스코프, 5분 캐시)
   * GET /certificate-cards/dealer-dashboard-summary
   * REQ-BE-001~007 — dealerCodes 비어있지 않은 회사만 200, 그 외 403(DEALER_ONLY).
   * @returns {Promise<{ success: boolean, data: {
   *   cards: { totalCount, monthCount, prevMonthCount, monthly: [{ym, count}] },
   *   ledger: { byStatus: [{status, lotCount, cap, issued}], remainingCap },
   *   workOrders: { pendingCount, oldestPendingAt, byLab: [{labId, labName, count, lastCreatedAt}] },
   *   funnel: { cap, cardsCreated, assigned, selfIssued },
   *   labs: { activeCount, totalCount },
   *   hengjiu: { totalCount },
   * } }>}
   */
  getDealerDashboardSummary() {
    return chinaInapi.get('/certificate-cards/dealer-dashboard-summary');
  },

  // ─── 본사 대시보드 (SPEC-CHINA-HQ-DASHBOARD-001) ─────────────────────

  /**
   * 본사 전용 대시보드 집계 조회 (MANUFACTURER 역할 중 /hq-dashboard 메뉴 보유 계정만, 회사 단위 5분 캐시)
   * GET /certificate-cards/hq-dashboard-summary
   * 인가 게이트는 컨트롤러의 @RequireMenu('/hq-dashboard') 단독(서비스 레이어 회사-경계 술어 없음).
   * @returns {Promise<{ success: boolean, data: {
   *   partners: { hospitalCount, labCount, newHospitalCount, newLabCount, dormantCount, inactiveCount },
   *   production: { thisMonth: {orderCount, teethCount}, totalCount, completedCount, pendingCount,
   *     monthly: [{month, orderCount, completedCount, pendingCount, teethCount}],
   *     byTreatment: [{treatmentType, teethCount, cardCount}] },
   *   hospitalActivity: [{companyId, name, type, registeredAt, firstOrderAt, lastOrderAt, orderCount}],
   *   labActivity: [...],
   *   newPartners: [...],
   *   timezone: string,
   * } }>}
   */
  getHqDashboardSummary() {
    return chinaInapi.get('/certificate-cards/hq-dashboard-summary');
  },
};

export default certificateCardApi;
